import * as Dialog from '@radix-ui/react-dialog';
import { useAdminJobDetailGraphQL } from '@/hooks/graphql/useAdminJobDetailGraphQL';
import { JobDataRenderer } from './JobDataRenderer';
import { JobState } from '@/hooks/graphql/useAdminJobsGraphQL';
import { formatDistanceToNow } from 'date-fns';

interface JobDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  jobId: string | null;
}

/**
 * Job Detail Modal
 * Feature: 073-job-queue-inspector
 *
 * Displays full job details including data/output payloads
 */
export function JobDetailModal({ isOpen, onClose, jobId }: JobDetailModalProps) {
  const { data, loading, error } = useAdminJobDetailGraphQL(jobId || '', {
    skip: !jobId || !isOpen,
  });

  const job = data?.adminJobDetail;

  // Extract error message from output if present
  const errorMessage = job?.output && typeof job.output === 'object' && 'message' in job.output
    ? String(job.output.message)
    : null;

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/80 z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed left-[50%] top-[50%] z-50 max-h-[85vh] w-[90vw] max-w-4xl translate-x-[-50%] translate-y-[-50%] flex flex-col rounded-lg border border-gray-200 bg-white p-6 shadow-lg overflow-y-auto data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <Dialog.Title className="text-lg font-semibold text-gray-900">
              Job Details
            </Dialog.Title>
            <Dialog.Close className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-blue-500">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              <span className="sr-only">Close</span>
            </Dialog.Close>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <p className="text-gray-500">Loading job details...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 font-medium">Failed to load job details</p>
              <p className="text-red-600 text-sm mt-1">{error.message}</p>
            </div>
          )}

          {/* Job Not Found */}
          {!loading && !error && !job && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-yellow-800">Job not found</p>
            </div>
          )}

          {/* Job Details */}
          {job && (
            <div className="space-y-6">
              {/* Basic Info Grid */}
              <div className="grid grid-cols-2 gap-4 bg-gray-50 rounded-lg p-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Job ID</p>
                  <p className="text-sm font-mono text-gray-900">{job.id}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Queue Name</p>
                  <p className="text-sm font-medium text-gray-900">{job.name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">State</p>
                  <p className="text-sm text-gray-900">{job.state}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Status</p>
                  <p className="text-sm text-gray-900">{job.status || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Priority</p>
                  <p className="text-sm text-gray-900">{job.priority}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Retries</p>
                  <p className="text-sm text-gray-900">
                    {job.retryCount} / {job.retryLimit}
                    {job.retryBackoff && <span className="text-xs text-gray-500 ml-2">(backoff)</span>}
                  </p>
                </div>
              </div>

              {/* Error Message (if failed) */}
              {job.state === JobState.FAILED && errorMessage && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-red-900 mb-2">Error Message</h4>
                  <p className="text-sm text-red-800 font-mono">{errorMessage}</p>
                </div>
              )}

              {/* Timing Info Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Created</p>
                  <p className="text-sm text-gray-900">
                    {formatDistanceToNow(new Date(job.createdOn), { addSuffix: true })}
                  </p>
                  <p className="text-xs text-gray-500">{new Date(job.createdOn).toLocaleString()}</p>
                </div>
                {job.startedOn && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Started</p>
                    <p className="text-sm text-gray-900">
                      {formatDistanceToNow(new Date(job.startedOn), { addSuffix: true })}
                    </p>
                    <p className="text-xs text-gray-500">{new Date(job.startedOn).toLocaleString()}</p>
                  </div>
                )}
                {job.completedOn && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Completed</p>
                    <p className="text-sm text-gray-900">
                      {formatDistanceToNow(new Date(job.completedOn), { addSuffix: true })}
                    </p>
                    <p className="text-xs text-gray-500">{new Date(job.completedOn).toLocaleString()}</p>
                  </div>
                )}
                {job.duration && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Duration</p>
                    <p className="text-sm text-gray-900">
                      {job.duration < 1000
                        ? `${job.duration}ms`
                        : job.duration < 60000
                        ? `${(job.duration / 1000).toFixed(2)}s`
                        : `${Math.floor(job.duration / 60000)}m ${Math.floor((job.duration % 60000) / 1000)}s`}
                    </p>
                  </div>
                )}
              </div>

              {/* Input Data */}
              <JobDataRenderer data={job.data} label="Input Data" />

              {/* Output Data */}
              <JobDataRenderer data={job.output} label="Output / Result" />
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end mt-6 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
            >
              Close
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
